import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useQuery } from 'react-query';
import { useFetchZonesAll } from '../Services/query';
import { getAgentZonesRequest, toggleAgentZoneRequest } from '../Services/request';

const ModalZoneToggle = ({ agent, onHide, toast }) => {
    const { data: zones } = useFetchZonesAll();
    const { data: linkedZones, isLoading } = useQuery(
        ['UseAgentZones', agent?.id],
        () => getAgentZonesRequest(agent.id),
        { enabled: Boolean(agent?.id) }
    );
    const [linkedIds, setLinkedIds] = useState(new Set());
    const [loadingZoneId, setLoadingZoneId] = useState(null);

    useEffect(() => {
        setLinkedIds(new Set((linkedZones || []).map((zone) => zone.id)));
    }, [linkedZones, agent?.id]);

    const sortedZones = useMemo(() => [...(zones || [])].sort((first, second) => {
        return Number(!linkedIds.has(first.id)) - Number(!linkedIds.has(second.id)) || first.name.localeCompare(second.name);
    }), [zones, linkedIds]);

    const toggle = async (zoneId) => {
        const wasLinked = linkedIds.has(zoneId);
        setLinkedIds((current) => {
            const next = new Set(current);
            wasLinked ? next.delete(zoneId) : next.add(zoneId);
            return next;
        });
        setLoadingZoneId(zoneId);
        try {
            await toggleAgentZoneRequest({ zoneId, agentId: agent.id });
            toast.current?.show({ severity: 'success', summary: 'Vínculo atualizado', detail: wasLinked ? 'Assistente removido da zona.' : 'Assistente adicionado à zona.', life: 2000 });
        } catch (error) {
            setLinkedIds((current) => {
                const next = new Set(current);
                wasLinked ? next.add(zoneId) : next.delete(zoneId);
                return next;
            });
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: error?.response?.data?.message || 'Não foi possível atualizar o vínculo.' });
        } finally {
            setLoadingZoneId(null);
        }
    };

    return (
        <Dialog visible={Boolean(agent)} onHide={onHide} modal header={`Zonas — ${agent?.name || ''}`} style={{ width: 480, maxWidth: '95vw' }}>
            {isLoading ? (
                <div className="flex justify-content-center p-5"><i className="pi pi-spin pi-spinner text-primary text-2xl" /></div>
            ) : sortedZones.length === 0 ? (
                <div className="text-center text-color-secondary p-5"><i className="pi pi-inbox text-2xl block mb-2" />Nenhuma zona cadastrada.</div>
            ) : (
                <div className="flex flex-column gap-2">
                    {sortedZones.map((zone) => {
                        const linked = linkedIds.has(zone.id);
                        return (
                            <div key={zone.id} className="flex align-items-center justify-content-between p-3 border-1 border-round-lg" style={{ borderColor: linked ? '#86efac' : 'var(--surface-border)', background: linked ? '#f0fdf4' : 'var(--surface-ground)' }}>
                                <span className={linked ? 'font-semibold text-green-700' : ''}><i className={`pi ${linked ? 'pi-check-circle text-green-600' : 'pi-circle text-color-secondary'} mr-2`} />{zone.name}</span>
                                <Button icon={loadingZoneId === zone.id ? 'pi pi-spin pi-spinner' : linked ? 'pi pi-minus' : 'pi pi-plus'} text rounded severity={linked ? 'danger' : 'success'} disabled={loadingZoneId !== null} onClick={() => toggle(zone.id)} />
                            </div>
                        );
                    })}
                    <small className="text-color-secondary mt-2">{linkedIds.size} de {sortedZones.length} zona(s) vinculada(s)</small>
                </div>
            )}
        </Dialog>
    );
};

export default ModalZoneToggle;
